import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';

interface FilterSelect {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  className?: string;
}

interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  /** Default: `t.common.search`. */
  searchPlaceholder?: string;
  /** The `data-testid` of the search box. */
  searchTestId?: string;
  selects?: FilterSelect[];
  onClear?: () => void;
  showClear?: boolean;
  resultsCount?: number | null;
  actions?: React.ReactNode;
}

export default function FilterBar({
  search, onSearchChange, searchPlaceholder, searchTestId,
  selects = [], onClear, showClear, resultsCount, actions,
}: FilterBarProps) {
  const { t } = useI18n();
  const { resultCount } = useLabels();
  const placeholder = searchPlaceholder ?? t.common.search;
  const hasFilters = search || selects.some(s => s.value);

  return (
    <>
      {/* #552 — on a phone the search takes a full line, and the selects share
          the next lines at an equal width (a select narrower than 8 rem moves
          to a new line). On a wide screen all items share one line. */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          placeholder={placeholder}
          aria-label={placeholder}
          data-testid={searchTestId}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          className="input grow basis-full sm:basis-0 sm:min-w-[200px]"
        />
        {selects.map((s, i) => (
          <select key={i} value={s.value} onChange={e => s.onChange(e.target.value)} className={s.className || 'input flex-1 min-w-32 sm:min-w-0 sm:flex-none sm:w-auto'}>
            <option value="">{s.placeholder}</option>
            {s.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            {/* #661 — a value that no option holds (a link with ?country=ZZ, a
                team that left the list) stays visible: the select told "All
                countries" over a list that the filter had cut. The select value
                must be human readable (a code such as a gender or a country,
                never an id), because the option shows the value as its text. */}
            {s.value && !s.options.some(o => o.value === s.value) && <option value={s.value}>{s.value}</option>}
          </select>
        ))}
        {showClear && hasFilters && onClear && (
          <button onClick={onClear} className="btn-outline btn-sm">{t.common.clear}</button>
        )}
        {actions}
      </div>
      {resultsCount != null && hasFilters && (
        <p className="text-xs text-gray-400">{resultCount(resultsCount)}</p>
      )}
    </>
  );
}
